// Import React and ReactDOM
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import Analytics
import { AnalyticsProvider, AnalyticsConsentBanner } from './utils/analytics.js';

// Import necessary components directly
import Navbar from './components/Navbar.js';
import Footer from './components/Footer.js';
import HomePage from './components/HomePage.js';
import HowItWorks from './components/HowItWorks.js';
import ImpactMetrics from './components/ImpactMetrics.js';
import AboutMission from './components/AboutMission.js';
import MissionPage from './components/MissionPage.js';
import Contact from './components/Contact.js';
import DonationsPage from './components/DonationsPage.js';
import EnhancedDonationPage from './components/EnhancedDonationPage.js';
import AuthWrapper from './components/Auth/AuthWrapper.js';
import AuthCallback from './components/Auth/AuthCallback.js';
import ResetPasswordForm from './components/Auth/ResetPasswordForm.js';
import LLMPage from './components/LLMPage.js';
import PartnerPage from './components/Partner/PartnerPage.js';
import PartnerWithUsPage from './components/Partner/PartnerWithUsPage.js';
import ESGPlatformPage from './components/ESG/ESGPlatformPage.js';
import PartnerPortal from './components/Partner/PartnerPortal.js';
import VolunteerPage from './components/VolunteerPage.js';
import VolunteerApplication from './components/VolunteerApplication.js';
import UserAccount from './components/User/AccountPage.js';
import AdminPanel from './components/Admin/AdminPanel.js';
import Technology from './components/Technology.js';
import Loading from './components/common/Loading.js';
import News from './components/News.js';
import AboutPage from './components/AboutPage.js';
import PrivacyPolicy from './components/PrivacyPolicy.js';
import TermsOfService from './components/TermsOfService.js';
import CookiePolicy from './components/CookiePolicy.js';
import Toast from './components/common/Toast.js';
import ImpactPage from './components/Impact/index.js';
import SustainableGames from './components/SustainableGames.js';
import SustainableGamesPage from './components/SustainableGamesPage.js';
import SequoiaAIPage from './components/SequoiaAIPage.js';

// Protected Route component
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return <Loading />;
    }
    
    if (!user) {
        return <Navigate to="/login" />;
    }
    
    // Clone children and pass the user prop to ensure authenticated pages have user context
    return React.Children.map(children, child => {
        // Check if it's a valid element before cloning
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { user });
        }
        return child;
    });
}

// Admin Route component
function AdminRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return <Loading />;
    }
    
    if (!user || user.role !== 'admin') {
        return <Navigate to="/" />;
    }
    
    return children;
}

// App Routes component
function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/impact" element={<ImpactPage />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/news" element={<News />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/sustainable-games" element={<SustainableGamesPage />} />
            
            {/* Sequoia AI is the only protected page */}
            <Route path="/sequoia-ai" element={
                <ProtectedRoute>
                    <SequoiaAIPage />
                </ProtectedRoute>
            } />
            
            {/* Auth Routes */}
            <Route path="/login" element={<AuthWrapper />} />
            <Route path="/register" element={<AuthWrapper />} />
            <Route path="/forgot-password" element={<AuthWrapper />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Previously protected routes - now public */}
            <Route path="/donate" element={<EnhancedDonationPage />} />
            <Route path="/llm" element={<LLMPage />} />
            <Route path="/partner" element={<PartnerWithUsPage />} />
            <Route path="/partner-legacy" element={<PartnerPage />} />
            <Route path="/esg-platform" element={<ESGPlatformPage />} />
            <Route path="/partner-portal" element={<PartnerPortal />} />
            <Route path="/volunteer" element={<VolunteerPage />} />
            <Route path="/volunteer-apply" element={<VolunteerApplication />} />
            <Route path="/account" element={<UserAccount />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
                <AdminRoute>
                    <AdminPanel />
                </AdminRoute>
            } />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
}

// Main App Component
function App() {
    return (
        <Router>
            <AuthProvider>
                <AnalyticsProvider>
                    <div className="app">
                        <Navbar />
                        <main className="main-content">
                            <AppRoutes />
                        </main>
                        <Footer />
                        <AnalyticsConsentBanner />
                    </div>
                </AnalyticsProvider>
            </AuthProvider>
        </Router>
    );
}

// Render the app
ReactDOM.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
    document.getElementById('root')
);
